<script setup lang="ts">
import { ProjectAvatar } from '@forgd/ui-kit/blocks/avatars'
import { NoSearchResults } from '@forgd/ui-kit/blocks/empty-states'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@forgd/ui-kit/combobox'
import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid'

type CryptoInfo = SuccessResponseBody<'/coins/{coingeckoId}'>['coin']
type CoinSearchResult = SuccessResponseBody<'/coins/search'>['coins'][number]

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  coingeckoId: string | null
}>()

const emit = defineEmits<{
  change: [coin: CryptoInfo]
}>()

const { open, query, debouncedQuery, filtered, loading, reset } = useAsyncComboboxFilter<CoinSearchResult>(
  async (q) => {
    const result = await apiFetch('/coins/search', {
      params: { query: { query: q } },
    })
    return result?.coins ?? []
  },
)

const selectedCoin = ref<CoinSearchResult | null>(null)

watch(() => props.coingeckoId, (id) => {
  if (!id) {
    selectedCoin.value = null
  }
})

async function handleSelect(opt: CoinSearchResult) {
  try {
    const result = await apiFetch('/coins/{coingeckoId}', {
      params: { path: { coingeckoId: opt.id } },
    })
    if (result?.coin) {
      selectedCoin.value = opt
      emit('change', result.coin)
    }
  }
  finally {
    reset()
  }
}
</script>

<template>
  <Combobox v-model:open="open">
    <ComboboxTrigger
      v-bind="$attrs"
      data-ui="CoinSelect"
      size="md"
      placeholder="Search token by name or ticker..."
    >
      <div v-if="selectedCoin" class="flex items-center gap-2">
        <ProjectAvatar :src="selectedCoin.image" alt="" size="sm" bordered />
        <span class="text-base text-brand-900">{{ selectedCoin.name }}</span>
        <span class="text-sm text-gray-600">{{ selectedCoin.symbol }}</span>
      </div>
      <template #icon>
        <MagnifyingGlassIcon class="opacity-50" />
      </template>
    </ComboboxTrigger>
    <ComboboxContent>
      <ComboboxInput
        v-model="query"
        :loading="loading"
        type="search"
        role="searchbox"
        placeholder="Type at least 2 characters to start searching"
      />
      <ComboboxList>
        <ComboboxEmpty
          v-if="loading"
          class="items-start justify-start px-2 py-1.5 text-left text-placeholder"
        >
          Loading...
        </ComboboxEmpty>
        <ComboboxEmpty
          v-else-if="filtered.length === 0 && debouncedQuery.length < 2"
          class="items-start justify-start px-2 py-1.5 text-left text-placeholder"
        >
          No options
        </ComboboxEmpty>
        <ComboboxEmpty v-else-if="filtered.length === 0" class="py-0">
          <NoSearchResults
            title="No results"
            message="Please modify your filter"
            @clear="query = ''"
          />
        </ComboboxEmpty>
        <template v-else>
          <ComboboxItem
            v-for="(opt, index) in filtered"
            :key="opt.id"
            :value="opt.id"
            :data-testid="`coin-select-option-${index}`"
            :selected="selectedCoin?.id === opt.id"
            @select="handleSelect(opt)"
          >
            <div class="flex items-center gap-2">
              <ProjectAvatar :src="opt.image" alt="" size="md" bordered />
              <span class="text-base text-brand-900">{{ opt.name }}</span>
              <span class="text-sm text-gray-600">{{ opt.symbol }}</span>
            </div>
          </ComboboxItem>
        </template>
      </ComboboxList>
    </ComboboxContent>
  </Combobox>
</template>
